import { useEffect, useState } from 'react'
import { useLoaderData, useNavigate } from 'react-router-dom'
import axios from 'axios'
import dayjs from 'dayjs'
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
} from '@mui/material'
import PageAppBar from 'components/PageAppBar'
import {
  ProfileActivityHistoryPair,
  ActivityModel,
  ActivityHistoryModel,
} from 'types'
import { base_url } from 'env'

export default function ActivityHistoryPreviewPage() {
  const navigate = useNavigate()
  const { profile, history } = useLoaderData() as ProfileActivityHistoryPair

  const [activityDetails, setActivityDetails] = useState<ActivityModel | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) return

    setIsLoading(true)
    axios
      .get(`${base_url}/activity/${history.activityId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setActivityDetails(res.data)
      })
      .catch((err) =>
        console.error("Impossible de charger les détails de l'activité", err)
      )
      .finally(() => setIsLoading(false))
  }, [history.activityId])

  const handleDelete = () => {
    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer cette entrée d'historique ?`
      )
    )
      return

    const token = localStorage.getItem('token')
    axios
      .delete(
        `${base_url}/profile/${profile.id}/activity/${(history as ActivityHistoryModel).id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then(() => {
        alert('Entrée supprimée avec succès.')
        navigate(`/profile/${profile.id}`)
      })
      .catch((err) => {
        console.error('La suppression a échoué:', err)
        alert('La suppression a échoué.')
      })
  }

  const renderContent = () => {
    if (isLoading) {
      return <CircularProgress />
    }

    return (
      <Card sx={{ p: 2, width: '100%', maxWidth: '800px' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Détails de la session d'activité
          </Typography>
          <Typography variant="h6" color="secondary">
            {activityDetails?.title || "Chargement de l'activité..."}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Pour {profile.firstName} {profile.lastName}
          </Typography>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <Chip
                label={`Date: ${dayjs(Number(history.date) * 1000).format('DD/MM/YYYY HH:mm')}`}
                variant="outlined"
                sx={{ width: '100%', p: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Chip
                label={`Durée: ${history.length} minutes`}
                variant="outlined"
                sx={{ width: '100%', p: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Chip
                label={`Points obtenus: ${history.giftedPoints}`}
                color="success"
                variant="outlined"
                sx={{ width: '100%', p: 1 }}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mt: 4 }}>
            Évaluations (sur 5)
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6} md={3}>
              <Chip
                label={`Difficulté: ${history.ratingDifficulty}`}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Chip
                label={`Appréciation: ${history.ratingEnjoyed}`}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Chip
                label={`Concentration: ${history.ratingFocused}`}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Chip
                label={`Durée: ${history.ratingLength}`}
                sx={{ width: '100%' }}
              />
            </Grid>
          </Grid>
        </CardContent>
        <CardActions>
          <Button
            size="small"
            onClick={() =>
              navigate(
                `/profile/${profile.id}/activity/${(history as ActivityHistoryModel).id}/edit`
              )
            }
          >
            Modifier
          </Button>
          <Button size="small" color="error" onClick={handleDelete}>
            Supprimer
          </Button>
        </CardActions>
      </Card>
    )
  }

  return (
    <PageAppBar pageName="Détail d'Activité">
      <Box
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
        }}
      >
        {renderContent()}
      </Box>
    </PageAppBar>
  )
}
